/**
 * The one way a browser will keep uploading after the page has stopped running.
 *
 * Everything else in the outbox survives interruption by resuming afterwards:
 * the bytes are chunked, the offsets are remembered, and a send picks up where
 * it left off the next time the app is opened. That is the best a web page can
 * do on its own, and on iOS it is the whole story — Safari has nothing like
 * this and is not going to.
 *
 * Where Background Fetch exists — Chrome, which on a phone means Android — it
 * is genuinely better: the request is handed to the browser, which carries it
 * out with the tab frozen, the app switched away from, or closed entirely, and
 * puts a progress notification in the shade while it does. The service worker
 * is woken when it finishes, and posts the message from there (see sw.ts), so a
 * video picked at the bus stop arrives whether or not anyone opens the app
 * again.
 *
 * One request per file rather than per chunk, and that is not an optimisation:
 * a background fetch runs its requests in whatever order it likes, and the
 * upload endpoint refuses anything that is not written at the offset it expects
 * (see appendFrom in server/src/uploads.ts). One request per file has no order
 * to get wrong — and because the server counts bytes as they land, a body cut
 * off half way is still progress the next attempt resumes from.
 */

/*
 * Nothing here runs inside the native shell, and the check below is what makes
 * that true rather than a hope: Background Fetch hangs off a service worker
 * registration, and there are no service workers on `capacitor://localhost`. So
 * the requests built further down stay relative and stay cookie-authenticated,
 * which is correct for the only build that reaches them. A native equivalent —
 * an upload the OS carries on with the app closed — is a plugin, not this.
 */

/** Background Fetch is not in lib.dom; only what is used here is declared. */
interface BackgroundFetchRegistration extends EventTarget {
  readonly id: string;
  readonly uploadTotal: number;
  readonly uploaded: number;
  readonly result: '' | 'success' | 'failure';
  abort(): Promise<boolean>;
}

interface BackgroundFetchManager {
  fetch(
    id: string,
    requests: Request[],
    options?: {
      title?: string;
      icons?: { src: string; sizes?: string; type?: string }[];
      downloadTotal?: number;
    },
  ): Promise<BackgroundFetchRegistration>;
  get(id: string): Promise<BackgroundFetchRegistration | undefined>;
  getIds(): Promise<string[]>;
}

type FetchCapableRegistration = ServiceWorkerRegistration & {
  backgroundFetch?: BackgroundFetchManager;
};

export type { BackgroundFetchRegistration };

/**
 * Whether this browser will do it at all.
 *
 * Checked on the prototype rather than on a live registration, so the answer is
 * available before the service worker is ready and a caller can decide without
 * waiting for one.
 */
export function supportsBackgroundFetch(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof ServiceWorkerRegistration !== 'undefined' &&
    'backgroundFetch' in ServiceWorkerRegistration.prototype
  );
}

/**
 * How long to wait for a service worker before giving up on one.
 *
 * `navigator.serviceWorker.ready` waits for an *activated* worker and does not
 * reject when there will never be one — a registration that failed to evaluate,
 * a private window, a browser with workers turned off. It simply never settles,
 * and awaiting it without this would hang the send that asked, permanently and
 * silently. The dev server reaches that state on every load, which is how this
 * was found.
 */
const READY_TIMEOUT_MS = 3_000;

async function manager(): Promise<BackgroundFetchManager | null> {
  if (!supportsBackgroundFetch()) return null;
  try {
    const registration = (await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), READY_TIMEOUT_MS)),
    ])) as FetchCapableRegistration | null;
    return registration?.backgroundFetch ?? null;
  } catch {
    // A browser that refused the registration outright. Nothing to hand to.
    return null;
  }
}

/** One file still to go up, as a background fetch needs it. */
export interface BackgroundPart {
  uploadId: string;
  offset: number;
  size: number;
  blob: Blob;
}

/**
 * Hands a send to the browser.
 *
 * Returns the registration when it took it, and null when it did not — an
 * unsupported browser, a quota refusal, or a send with nothing left to upload.
 * Every one of those is ordinary, and the answer to all of them is the same:
 * carry on uploading here.
 */
export async function handOver(
  id: string,
  parts: BackgroundPart[],
  title: string,
): Promise<BackgroundFetchRegistration | null> {
  const bg = await manager();
  if (!bg) return null;

  const requests = parts
    .filter((part) => part.offset < part.size)
    .map(
      (part) =>
        new Request(`/api/uploads/${part.uploadId}?offset=${part.offset}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/octet-stream' },
          body: part.blob.slice(part.offset),
          // Same-origin, but stated: the upload endpoint is authenticated with
          // the session cookie like everything else, and a background fetch
          // that forgets it would be a run of 401s in the notification shade.
          credentials: 'include',
        }),
    );
  if (requests.length === 0) return null;

  try {
    return await bg.fetch(id, requests, {
      title,
      icons: [{ src: '/icons/icon-192.png', sizes: '192x192', type: 'image/png' }],
      // The responses, not the upload: this API measures downloads, and the
      // numbers worth watching for a send are uploaded/uploadTotal below.
      downloadTotal: 0,
    });
  } catch {
    // A fetch already registered under this id, or the browser declining to
    // start one. Both mean the foreground keeps it.
    return null;
  }
}

/** A running background fetch for this send, if the browser still has one. */
export async function running(id: string): Promise<BackgroundFetchRegistration | null> {
  const bg = await manager();
  if (!bg) return null;
  try {
    const found = await bg.get(id);
    return found?.result === '' ? found : null;
  } catch {
    return null;
  }
}

/** Stops one, for a send that is being given up on. */
export async function cancel(id: string): Promise<void> {
  const found = await running(id);
  await found?.abort().catch(() => {});
}
